import React, { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { key: "fan", label: "Fan", price: "$5" },
  { key: "patron", label: "Patron", price: "$18" },
  { key: "producer", label: "Producer", price: "$36" },
  { key: "custom", label: "Your call", price: "$…" },
];

/**
 * Picks a tier (or a pay-what-you-want amount) and hands off to Stripe
 * Checkout for a monthly subscription. The name and region typed here are what
 * appear on the patrons wall.
 */
export default function PatronageDialog({ open, onOpenChange, initialTier = "patron" }) {
  const [tier, setTier] = useState(initialTier);
  const [custom, setCustom] = useState("25");
  const [displayName, setDisplayName] = useState("");
  const [region, setRegion] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) setTier(initialTier);
  }, [open, initialTier]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const amount = Number(custom);
    if (tier === "custom" && (!amount || amount < 2 || amount > 1000)) {
      toast.error("Pick an amount between $2 and $1,000 a month.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/patronage/checkout`, {
        tier,
        custom_amount: tier === "custom" ? amount : null,
        display_name: displayName.trim(),
        region: region.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reach checkout. Try again in a moment.");
      setBusy(false);
    }
  };

  const goldButton = {
    backgroundColor: "#F2D589",
    color: "#080419",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    fontWeight: 700,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-white/15 max-w-lg"
        style={{ backgroundColor: "#0C0826", color: "#F5EFE3" }}
        data-testid="patronage-dialog"
      >
        <DialogHeader>
          <p className="text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "#F2D589" }}>
            Support the music
          </p>
          <DialogTitle
            className="text-2xl sm:text-3xl leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Pick your monthly amount.
          </DialogTitle>
          <DialogDescription className="text-sm mt-2" style={{ color: "rgba(245,239,227,0.75)" }}>
            Billed monthly, cancel any time. Your name goes on the wall exactly
            as you write it below.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4 mt-1" data-testid="patronage-form">
          <div className="grid grid-cols-4 gap-2" data-testid="patronage-tier-group">
            {TIERS.map((t) => {
              const active = tier === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTier(t.key)}
                  data-testid={`patronage-tier-${t.key}`}
                  className="rounded-xl px-2 py-3 text-center transition-colors"
                  style={{
                    border: `1px solid ${active ? "#F2D589" : "rgba(255,255,255,0.15)"}`,
                    backgroundColor: active ? "rgba(242,213,137,0.12)" : "transparent",
                  }}
                >
                  <span
                    className="block"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: "1.4rem",
                      color: active ? "#F2D589" : "#F5EFE3",
                    }}
                  >
                    {t.price}
                  </span>
                  <span className="block text-[10px] uppercase tracking-[0.12em] mt-1" style={{ color: "rgba(245,239,227,0.55)" }}>
                    {t.label}
                  </span>
                </button>
              );
            })}
          </div>

          {tier === "custom" ? (
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,239,227,0.6)" }}>
                Dollars per month
              </span>
              <Input
                type="number"
                min="2"
                max="1000"
                step="1"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                data-testid="patronage-custom-amount"
                className="h-11 bg-white/[0.06] border-white/15 text-white"
              />
            </label>
          ) : null}

          {/* autoComplete off on both: browser autofill used to drop an email
              address in here, and these two values are shown publicly. */}
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,239,227,0.6)" }}>
              Name for the wall
            </span>
            <Input
              type="text"
              name="wall-name"
              autoComplete="off"
              maxLength={60}
              placeholder="e.g. Dan B."
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="patronage-display-name"
              className="h-11 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: "rgba(245,239,227,0.6)" }}>
              Where you&apos;re from (optional)
            </span>
            <Input
              type="text"
              name="wall-region"
              autoComplete="off"
              maxLength={40}
              placeholder="e.g. Boca Raton, FL"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              data-testid="patronage-region"
              className="h-11 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
            />
          </label>
          <p className="text-[11px] -mt-1" style={{ color: "rgba(245,239,227,0.45)" }}>
            Both of these show publicly on the patrons wall — no email addresses
            or phone numbers, they&apos;ll be dropped.
          </p>

          <button
            type="submit"
            disabled={busy}
            data-testid="patronage-submit"
            className="h-12 rounded-full text-xs"
            style={goldButton}
          >
            {busy ? "Opening checkout…" : "Continue to payment →"}
          </button>
          <p className="text-[11px] text-center" style={{ color: "rgba(245,239,227,0.5)" }}>
            Secure checkout by Stripe. Cancel any time by emailing Dan.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
